import { Component } from '@theme/component';

/**
 * Foto-Laufband: laeuft langsam von selbst und laesst sich ziehen.
 *
 * Die Bewegung ist eine reine Verschiebung (transform, GPU), pro Frame vom Skript gesetzt.
 * So kann das Band jederzeit angehalten, gezogen (Maus, Finger, Trackpad, Pfeiltasten) und
 * nahtlos von der neuen Stelle aus fortgesetzt werden. Die Reihe steht doppelt im Markup;
 * nach einer halben Laenge springt die Position unsichtbar zurueck.
 *
 * Haelt an (NOVELLINO: nicht mehr beim Ueberfahren): nur waehrend Klicken-und-Halten bzw. Ziehen, per Pause-Knopf (Pflicht bei
 * automatischer Bewegung), ausserhalb des Sichtbereichs und bei „Bewegung reduzieren“.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [toggle]
 * @property {HTMLElement} [toggleLabel]
 * @property {HTMLElement} [viewport]
 * @property {HTMLElement} [track]
 * @property {HTMLDialogElement} [lightbox]
 * @property {HTMLImageElement} [lightboxImage]
 * @property {HTMLElement} [lightboxCount]
 * @property {HTMLButtonElement} [lightboxClose]
 * @property {HTMLButtonElement} [lightboxPrev]
 * @property {HTMLButtonElement} [lightboxNext]
 * @property {HTMLTemplateElement} [lightboxData]
 *
 * @extends {Component<Refs>}
 */
class PhotoMarqueeComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #visibility = null;

  #offset = 0;
  #lastTime = 0;
  #frame = 0;
  #visible = false;
  #hovered = false;
  #paused = false;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** @type {{ pointerId: number, x: number } | null} */
  #drag = null;
  #resumeTimer = 0;
  #touchHold = false;

  /** NOVELLINO: Lightbox */
  /** @type {{ x: number, t: number, index: number } | null} */
  #tap = null;
  #lightboxIndex = 0;
  /** @type {{ src: string, alt: string }[]} */
  #photos = [];
  #swipeX = 0;

  connectedCallback() {
    super.connectedCallback();
    const viewport = this.refs.viewport;
    if (!viewport) return;

    this.#visibility = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      if (this.#visible) this.#start();
    });
    this.#visibility.observe(this);

    viewport.addEventListener('pointerenter', this.#handlePointerEnter);
    viewport.addEventListener('pointerleave', this.#handlePointerLeave);
    viewport.addEventListener('pointerdown', this.#handlePointerDown);
    viewport.addEventListener('pointermove', this.#handlePointerMove);
    viewport.addEventListener('pointerup', this.#handlePointerUp);
    viewport.addEventListener('pointercancel', this.#handlePointerUp);
    viewport.addEventListener('wheel', this.#handleWheel, { passive: false });
    viewport.addEventListener('keydown', this.#handleKeydown);
    this.#setupLightbox();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#visibility?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#resumeTimer);
  }

  togglePlayback() {
    const toggle = this.refs.toggle;
    if (!toggle) return;
    this.#paused = !this.#paused;
    this.toggleAttribute('data-paused', this.#paused);
    toggle.setAttribute('aria-pressed', String(this.#paused));
    if (this.refs.toggleLabel) {
      this.refs.toggleLabel.textContent = (this.#paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause) ?? '';
    }
    this.#start();
  }

  /* ---------- Bewegung ---------- */

  get #halfWidth() {
    return (this.refs.track?.scrollWidth ?? 0) / 2;
  }

  get #speed() {
    const duration = Number.parseFloat(this.dataset.duration ?? '70') || 70;
    // NOVELLINO: Tempo je Foto konstant, unabhaengig davon, wie oft die Reihe wiederholt wird
    const reps = Number.parseInt(this.dataset.reps ?? '1', 10) || 1;
    return this.#halfWidth / reps / duration; // Pixel pro Sekunde
  }

  #isRunning() {
    return (
      this.#visible &&
      !this.#paused &&
      !this.#hovered &&
      !this.#drag &&
      !this.#touchHold &&
      !this.#reducedMotion &&
      !this.refs.lightbox?.open
    );
  }

  #start() {
    if (this.#frame) return;
    this.#lastTime = performance.now();
    this.#frame = requestAnimationFrame(this.#tick);
  }

  /** @param {number} time */
  #tick = (time) => {
    const delta = Math.min(time - this.#lastTime, 100) / 1000;
    this.#lastTime = time;
    if (this.#isRunning()) {
      const direction = this.dataset.direction === 'right' ? -1 : 1;
      this.#move(this.#speed * delta * direction);
    }
    // Nur weiterlaufen, solange etwas passieren kann – sonst ruht der Frame-Takt.
    if (this.#isRunning() || this.#drag) {
      this.#frame = requestAnimationFrame(this.#tick);
    } else {
      this.#frame = 0;
    }
  };

  /** @param {number} distance – positiv verschiebt nach links */
  #move(distance) {
    const half = this.#halfWidth;
    if (!half || !this.refs.track) return;
    this.#offset = (((this.#offset + distance) % half) + half) % half;
    this.refs.track.style.transform = `translate3d(${-this.#offset}px, 0, 0)`;
  }

  /* ---------- Bedienung ---------- */

  /** @param {PointerEvent} event */
  #handlePointerEnter = () => {
    // NOVELLINO: Ueberfahren haelt das Band nicht mehr an – nur Klicken und Halten
  };

  /** @param {PointerEvent} event */
  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = false;
    this.#start();
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (event.button !== 0) return;
    clearTimeout(this.#resumeTimer);
    this.#drag = { pointerId: event.pointerId, x: event.clientX };
    const item = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.photo-marquee__item') : null
    );
    this.#tap = item ? { x: event.clientX, t: performance.now(), index: Number(item.dataset.index) || 0 } : null;
    this.#touchHold = event.pointerType !== 'mouse';
    this.refs.viewport?.setPointerCapture(event.pointerId);
    this.toggleAttribute('data-dragging', true);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) return;
    const dx = event.clientX - this.#drag.x;
    this.#drag.x = event.clientX;
    this.#move(-dx);
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) return;
    this.#drag = null;
    this.toggleAttribute('data-dragging', false);
    // NOVELLINO: Kurzer Klick ohne Ziehen oeffnet die Lightbox; Halten oder Ziehen nicht
    if (this.#tap && Math.abs(event.clientX - this.#tap.x) < 6 && performance.now() - this.#tap.t < 350) {
      this.#openLightbox(this.#tap.index);
    }
    this.#tap = null;
    // NOVELLINO: Nach dem Loslassen sofort weiterlaufen (auch auf dem Handy)
    this.#touchHold = false;
    this.#start();
  };

  /** Horizontales Trackpad-Wischen verschiebt das Band; senkrechtes Scrollen bleibt der Seite. */
  /** @param {WheelEvent} event */
  #handleWheel = (event) => {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    event.preventDefault();
    this.#move(event.deltaX);
  };

  /* ---------- NOVELLINO: Lightbox ---------- */

  #setupLightbox() {
    const { lightbox, lightboxData, lightboxClose, lightboxPrev, lightboxNext, lightboxImage } = this.refs;
    if (!lightbox || !lightboxData) return;
    this.#photos = [...lightboxData.content.querySelectorAll('span')].map((el) => ({
      src: el.getAttribute('data-src') ?? '',
      alt: el.getAttribute('data-alt') ?? '',
    }));
    // showModal() legt den Dialog in die oberste Ebene – kein Eltern-Element kann ihn beschneiden
    lightboxClose?.addEventListener('click', () => lightbox.close());
    lightboxPrev?.addEventListener('click', () => this.#showPhoto(this.#lightboxIndex - 1));
    lightboxNext?.addEventListener('click', () => this.#showPhoto(this.#lightboxIndex + 1));
    lightbox.addEventListener('click', (event) => {
      // Klick auf den dunklen Hintergrund (nicht aufs Foto oder die Knoepfe) schliesst
      if (event.target === lightbox || event.target === lightbox.querySelector('.photo-lightbox__figure')) lightbox.close();
    });
    lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.#showPhoto(this.#lightboxIndex - 1);
      if (event.key === 'ArrowRight') this.#showPhoto(this.#lightboxIndex + 1);
    });
    lightbox.addEventListener('close', () => this.#start());
    lightboxImage?.addEventListener('pointerdown', (event) => {
      this.#swipeX = event.clientX;
    });
    lightboxImage?.addEventListener('pointerup', (event) => {
      const dx = event.clientX - this.#swipeX;
      if (Math.abs(dx) > 50) this.#showPhoto(this.#lightboxIndex + (dx < 0 ? 1 : -1));
    });
  }

  /** @param {number} index */
  #openLightbox(index) {
    const lightbox = this.refs.lightbox;
    if (!lightbox || !this.#photos.length) return;
    this.#showPhoto(index);
    if (!lightbox.open) lightbox.showModal();
    this.refs.lightboxClose?.focus();
  }

  /** @param {number} index */
  #showPhoto(index) {
    const count = this.#photos.length;
    if (!count) return;
    this.#lightboxIndex = ((index % count) + count) % count;
    const photo = this.#photos[this.#lightboxIndex];
    if (this.refs.lightboxImage && photo) {
      this.refs.lightboxImage.src = photo.src;
      this.refs.lightboxImage.alt = photo.alt;
    }
    if (this.refs.lightboxCount) this.refs.lightboxCount.textContent = `${this.#lightboxIndex + 1} / ${count}`;
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      // Tastatur: Enter oeffnet das erste sichtbare Foto
      const target = event.target instanceof Element ? event.target.closest('.photo-marquee__item') : null;
      event.preventDefault();
      this.#openLightbox(target instanceof HTMLElement ? Number(target.dataset.index) || 0 : 0);
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.#move(event.key === 'ArrowRight' ? 240 : -240);
  };
}

if (!customElements.get('photo-marquee-component')) {
  customElements.define('photo-marquee-component', PhotoMarqueeComponent);
}
